import { config } from '../config';
import DigiobcanApi from './api/DigiobcanApi';
import ConsentModal from './components/ConsentModal';
import Layout from './components/Layout/Layout';
import RouteMetadata from './components/Layout/RouteMetadata';
import { ADMIN_CRUMBS } from './components/Layout/crumbs';
import PermissionsGuard from './components/PermissionsGuard';
import PrivatePages from './components/PrivatePages';
import QueryProviderNoCache from './components/QueryProviderNoCache';
import Redirect from './components/Redirect';
import ReportCategoriesGuard from './components/ReportCategoriesGuard';
import SnackbarAction from './components/SnackbarAction';
import ConsentApiTokenManager from './lib/ConsentApiTokenManager';
import CoreApiTokenManager from './lib/CoreApiTokenManager';
import LoggerApiTokenManager from './lib/LoggerApiTokenManager';
import Cookies from './lib/cookies';
import FrontChannelLogoutPage from './pages/FrontChannelLogout';
import HomePage from './pages/HomePage';
import LegacyRedirect from './pages/LegacyRedirect';
import NewsDetailPage from './pages/NewsDetailPage';
import NotFoundPage from './pages/NotFoundPage';
import PlusCodeRedirect from './pages/PlusCodeRedirect';
import AiDecisionsAdminPage from './pages/administracia/ai/AiDecisionsAdminPage';
import VotingAdminPage from './pages/administracia/hlasovania/VotingAdminPage';
import VotingDetailAdminPage from './pages/administracia/hlasovania/VotingDetailAdminPage/VotingDetailAdminPage';
import NewsCategoriesAdminPage from './pages/administracia/kategorie-novinky/NewsCategoriesAdminPage';
import NewsCategoryAdminPage from './pages/administracia/kategorie-novinky/[categoryId]/NewsCategoryAdminPage';
import ReportsCategoriesAdminPage from './pages/administracia/kategorie-podnetov/ReportsCategoriesAdminPage';
import ReportCategoryAdminPage from './pages/administracia/kategorie-podnetov/[categoryId]/ReportCategoryAdminPage';
import ReportsDetailPageAdmin from './pages/administracia/podnety/ReportDetailAdminPage/ReportDetailAdminPage';
import ReportsAdminPage from './pages/administracia/podnety/ReportsAdminPage';
import ProjectDetailAdminPage from './pages/administracia/projekty/ProjectDetailAdminPage';
import ProjectsAdminPage from './pages/administracia/projekty/ProjectsAdminPage';
import VyhodyAdminPage from './pages/administracia/vyhody/VyhodyAdminPage';
import VyhodaDetailAdminPage from './pages/administracia/vyhody/VyhodyDetailAdminPage';
import VotingsPage from './pages/hlasovania/VotingsPage';
import VotingDetailPage from './pages/hlasovania/[id]/VotingDetailPage';
import VotingResultsPage from './pages/hlasovania/[id]/VotingResultsPage';
import VotingRunPage from './pages/hlasovania/[id]/VotingRunPage';
import ReportDetailRedirect from './pages/podnety/[id]/ReportDetailRedirect';
import ReportCreatePage from './pages/podnety/novy/ReportCreatePage';
import ProfilePage from './pages/profil/ProfilePage';
import ProfileRedirect from './pages/profil/ProfileRedirect';
import { DEFAULT_PROFILE_SECTION, profilePath } from './pages/profil/sections';
import ProjectDetailPage from './pages/projekty/ProjectDetailPage';
import ProjectsPage from './pages/projekty/ProjectsPage';
import VyhodyPage from './pages/vyhody/VyhodyPage';
import GamificationProvider from './providers/GamificationProvider';
import HomePageLocationProvider from './providers/HomePageProvider';
import LayoutProvider from './providers/LayoutProvider';
import GeoLocationProvider from './providers/LocationProvider';
import LoggerProvider from './providers/LoggerProvider';
import MqttClientProvider from './providers/MqttClientProvider';
import NetworkProvider from './providers/NetworkProvider';
import NotificationsProvider from './providers/NotificationsProvider';
import RecaptchaWrapper from './providers/RecaptchaWrapper';
import ResponsiveProvider from './providers/ResponsiveProvider';
import WebPushProvider from './providers/WebPushProvider';
import theme from './theme/theme';
import { __ } from './utils';
import '@arcgis/core/assets/esri/themes/light/main.css';
import { AuthProvider, RolesProvider } from '@presov.sk/presov-auth-react';
import {
  CssBaseline,
  GlobalStyles,
  ThemeProvider,
} from '@presov.sk/presov-ui-react';
import '@presov.sk/presov-ui-react/css/markdown-editor.css';
import '@presov.sk/presov-ui-react/css/markdown-preview.css';
import '@presov.sk/presov-ui-react/theme/fonts.css';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { SnackbarProvider } from 'notistack';
import { BrowserRouter, Navigate, Route, Routes } from 'react-router';
import 'yet-another-react-lightbox/styles.css';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1 * (60 * 1000), // 1 min
      gcTime: 1 * (60 * 1000), // 1 min
      refetchOnWindowFocus: false,
      retry: 1,
    },
  },
});

CoreApiTokenManager.setAuthServer(config.AUTH_SERVER);
CoreApiTokenManager.setClientId(config.CLIENT_ID);

LoggerApiTokenManager.setAuthServer(config.AUTH_SERVER);
LoggerApiTokenManager.setClientId(config.CLIENT_ID);

ConsentApiTokenManager.setAuthServer(config.AUTH_SERVER);
ConsentApiTokenManager.setClientId(config.CLIENT_ID);

const App = () => {
  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider
        clientId={config.CLIENT_ID}
        authServer={config.AUTH_SERVER}
        onAuthorize={({ accessToken }) => {
          DigiobcanApi.setToken(accessToken);
          CoreApiTokenManager.setOriginalToken(accessToken);
          LoggerApiTokenManager.setOriginalToken(accessToken);
          ConsentApiTokenManager.setOriginalToken(accessToken);
        }}
        optional={true}
      >
        <LoggerProvider>
          <NetworkProvider>
            <ThemeProvider theme={theme}>
              <ResponsiveProvider>
                <MqttClientProvider>
                  <WebPushProvider>
                    <SnackbarProvider
                      maxSnack={3}
                      action={(snackbarId) => (
                        <SnackbarAction snackbarId={snackbarId} />
                      )}
                      classes={{
                        containerRoot: 'snackbar-container',
                      }}
                    >
                      <GeoLocationProvider>
                        <BrowserRouter>
                          <LayoutProvider>
                            <RolesProvider crmApiUrl={config.VITE_CORE_API_URL}>
                              <GamificationProvider>
                                <NotificationsProvider>
                                  <HomePageLocationProvider>
                                    <Layout>
                                      <ConsentModal />
                                      {/* <ReactQueryDevtools buttonPosition='top-left' /> */}
                                      <CssBaseline />
                                      <Cookies />
                                      <GlobalStyles
                                        styles={{
                                          '*': { boxSizing: 'border-box' },
                                          '*::before': {
                                            boxSizing: 'border-box',
                                          },
                                          '*::after': {
                                            boxSizing: 'border-box',
                                          },
                                          // Bez natívneho obnovenia ťahom nadol.
                                          // Obnovenie je vlastný `Pulldown`, ktorý
                                          // vie, kde sa ťahať nesmie; prehliadač to
                                          // nevie a chytal aj zatváranie spodných
                                          // listov. Stránka sama neskroluje
                                          // (skroluje obal shellu), takže sa tým nič
                                          // iné nemení.
                                          'html, body': {
                                            overscrollBehaviorY: 'none',
                                          },
                                          // Force landscape orientation for printing
                                          '@page': {
                                            size: 'A4',
                                            margin: '6mm',
                                          },
                                          // Visibility helpers for print vs. screen
                                          '.print-only': {
                                            display: 'none !important',
                                          },
                                          '@media print': {
                                            'html, body, #root': {
                                              height: 'auto !important',
                                              minHeight: 'auto !important',
                                              overflow: 'visible !important',
                                            },
                                            // Make all inner scroll containers expand for print
                                            'body *': {
                                              overflow: 'visible !important',
                                            },
                                            // Neutralize viewport/percent heights to avoid clipping to one page
                                            '[style*="height: 100dvh"], [style*="height:100dvh"]':
                                              {
                                                height: 'auto !important',
                                              },
                                            '[style*="height: 100vh"], [style*="height:100vh"]':
                                              {
                                                height: 'auto !important',
                                              },
                                            '[style*="overflow: auto"], [style*="overflow:auto"]':
                                              {
                                                overflow: 'visible !important',
                                              },
                                            '[style*="overflow-y: auto"], [style*="overflow-y:auto"]':
                                              {
                                                overflowY: 'visible !important',
                                              },
                                            '.screen-only': {
                                              display: 'none !important',
                                            },
                                            '.print-only': {
                                              display: 'inline-flex !important',
                                            },
                                            // Optional: hide overlays that could block content in print
                                            '.MuiDrawer-root, .MuiModal-root': {
                                              display: 'none !important',
                                            },
                                          },
                                          // Odsadenia pod hlavičkou.
                                          //
                                          // `--motd-h` je skutočná výška pásu
                                          // so správami; nastavuje ju
                                          // `MessagesBanner` cez ResizeObserver
                                          // a keď sa nekreslí nič, je to 0px.
                                          //
                                          // Predtým tu boli natvrdo dopočítané
                                          // hodnoty (114/106/99 vs 80/72/65)
                                          // podľa CUSTOM_SHOW_DEV_BANNER. Banner
                                          // je dátový, takže jeho prítomnosť sa
                                          // dopredu nedá vypočítať - a markdown
                                          // správa sa môže zabaliť do dvoch
                                          // riadkov, takže by nestačila ani
                                          // konštanta.
                                          '.snackbar-container': {
                                            // Doteraz nebolo o banneri vôbec
                                            // informované a pri zapnutom páse
                                            // prekrývalo hlavičku.
                                            top: 'calc(64px + var(--motd-h, 0px))',
                                            right: 8,
                                            zIndex: 1400,
                                          },
                                          '.grecaptcha-badge': {
                                            top: 'calc(80px + var(--motd-h, 0px)) !important',
                                            bottom: 'auto !important',
                                            '@media (max-width: 900px)': {
                                              top: 'calc(72px + var(--motd-h, 0px)) !important',
                                            },
                                            '@media (max-width: 600px)': {
                                              top: 'calc(65px + var(--motd-h, 0px)) !important',
                                            },
                                          },
                                        }}
                                      />
                                      <Routes>
                                        <Route
                                          path='/front-channel-logout'
                                          element={<FrontChannelLogoutPage />}
                                        />

                                        {/*
                                          Mapa a detail podnetu nad ňou sú
                                          jedna stránka s dvomi adresami:
                                          `/` a `/podnety/:reportId`. Layout
                                          routa bez cesty drží `HomePage`
                                          namontovanú, takže otvorenie
                                          a zavretie podnetu mení len adresu -
                                          mapa sa znova nekreslí. Deti nič
                                          nekreslia, sú tu len kvôli zhode.
                                          Detail v [[HomePage]].
                                        */}
                                        <Route
                                          element={
                                            <RouteMetadata
                                              root
                                              withoutLayoutPadding
                                            >
                                              <HomePage />
                                            </RouteMetadata>
                                          }
                                        >
                                          <Route path='/' element={null} />
                                          <Route
                                            path='/podnety/:reportId'
                                            element={null}
                                          />
                                        </Route>
                                        <Route
                                          path='/_auth'
                                          element={
                                            <RouteMetadata
                                              root
                                              withoutLayoutPadding
                                            >
                                              <HomePage />
                                            </RouteMetadata>
                                          }
                                        />

                                        <Route
                                          path='/plus.code/:plusCode/*'
                                          element={<PlusCodeRedirect />}
                                        />

                                        <Route
                                          path='/novinky'
                                          element={<Redirect to='/' />}
                                        />

                                        <Route
                                          path='/novinky/:id'
                                          element={
                                            <RouteMetadata backPath='/'>
                                              <NewsDetailPage />
                                            </RouteMetadata>
                                          }
                                        />

                                        {/* Výhody sú dočasne skryté - pozri config.CUSTOM_FEATURES. */}
                                        {config.CUSTOM_FEATURES.BENEFITS && (
                                          <>
                                            <Route
                                              path='/vyhody'
                                              element={
                                                <RouteMetadata
                                                  root
                                                  title={__('Vyhody')}
                                                  description={__(
                                                    'Zľavy a výhody pre obyvateľov Prešova od mesta a jeho partnerov.',
                                                  )}
                                                >
                                                  <VyhodyPage />
                                                </RouteMetadata>
                                              }
                                            />
                                          </>
                                        )}

                                        {/* Projekty sú dočasne skryté - pozri config.CUSTOM_FEATURES. */}
                                        {config.CUSTOM_FEATURES.PROJECTS && (
                                          <>
                                            <Route
                                              path='/projekty'
                                              element={
                                                <RouteMetadata
                                                  root
                                                  title={__('Projekty')}
                                                  description={__(
                                                    'Projekty mesta Prešov - čo sa v meste pripravuje a stavia, v akom je to stave a kde to bude.',
                                                  )}
                                                >
                                                  <ProjectsPage />
                                                </RouteMetadata>
                                              }
                                            />

                                            <Route
                                              path='/projekty/:projectId'
                                              element={
                                                <RouteMetadata
                                                  title={__('Detail projektu')}
                                                >
                                                  <ProjectDetailPage />
                                                </RouteMetadata>
                                              }
                                            />
                                          </>
                                        )}

                                        <Route
                                          path='/hlasovania'
                                          element={
                                            <RouteMetadata
                                              root
                                              title={__('Hlasovania')}
                                              description={__(
                                                'Hlasovania mesta Prešov - vyberte, o čom sa v meste rozhodne, a pozrite si výsledky uzavretých hlasovaní.',
                                              )}
                                              withoutLayoutPadding
                                            >
                                              <VotingsPage />
                                            </RouteMetadata>
                                          }
                                        />
                                        <Route
                                          path='/hlasovania/:votingId'
                                          element={
                                            <RouteMetadata
                                              title={__('Hlasovanie')}
                                            >
                                              <VotingDetailPage />
                                            </RouteMetadata>
                                          }
                                        />
                                        <Route
                                          path='/hlasovania/:votingId/vysledky'
                                          element={
                                            <RouteMetadata
                                              title={__('Výsledky hlasovania')}
                                            >
                                              <VotingResultsPage />
                                            </RouteMetadata>
                                          }
                                        />
                                        {/* Sústredený režim stojí mimo shellu -
                                            `Layout` ho podľa `isVotingRunPath`
                                            nekreslí, takže `RouteMetadata` by
                                            tu nemalo čo naplniť.

                                            Hlasovanie, ktoré to dovolí, smie
                                            odhlasovať aj neprihlásený - a ten
                                            musí priniesť reCAPTCHA token.
                                            Wrapper pre prihláseného nič
                                            nemountuje. */}
                                        <Route
                                          path='/hlasovania/:votingId/hlasovat'
                                          element={
                                            <RecaptchaWrapper>
                                              <VotingRunPage />
                                            </RecaptchaWrapper>
                                          }
                                        />

                                        {/*
                                          Moje podnety už nie sú samostatná
                                          stránka - sú prvou sekciou profilu
                                          ([[ProfilePage]]) a z hlavného menu
                                          zmizli. Obe adresy zostávajú, lebo na
                                          ne ukazujú rozposlané odkazy aj
                                          uložené záložky.
                                        */}
                                        <Route
                                          path='/podnety'
                                          element={
                                            <Redirect
                                              to={profilePath(
                                                DEFAULT_PROFILE_SECTION,
                                              )}
                                            />
                                          }
                                        />

                                        <Route
                                          path='/podnety/novy'
                                          element={
                                            <RouteMetadata
                                              backPath={profilePath(
                                                DEFAULT_PROFILE_SECTION,
                                              )}
                                              title={__('Nový podnet')}
                                              description={__(
                                                'Nahláste problém v Prešove - fotka, krátky popis a poloha na mape stačia. Podnet môžete podať aj bez prihlásenia.',
                                              )}
                                            >
                                              <RecaptchaWrapper>
                                                <ReportCreatePage />
                                              </RecaptchaWrapper>
                                            </RouteMetadata>
                                          }
                                        />
                                        <Route
                                          path='/moje-podnety'
                                          element={
                                            <Redirect
                                              to={profilePath(
                                                DEFAULT_PROFILE_SECTION,
                                              )}
                                            />
                                          }
                                        />
                                        <Route
                                          path='/moje-podnety/:reportId'
                                          element={<ReportDetailRedirect />}
                                        />

                                        <Route
                                          path='/moje-podnety/novy'
                                          element={
                                            <RouteMetadata
                                              backPath={profilePath(
                                                DEFAULT_PROFILE_SECTION,
                                              )}
                                              title={__('Nový podnet')}
                                              description={__(
                                                'Nahláste problém v Prešove - fotka, krátky popis a poloha na mape stačia. Podnet môžete podať aj bez prihlásenia.',
                                              )}
                                            >
                                              <RecaptchaWrapper>
                                                <ReportCreatePage />
                                              </RecaptchaWrapper>
                                            </RouteMetadata>
                                          }
                                        />

                                        <Route element={<PrivatePages />}>
                                          <Route
                                            path='/administracia'
                                            element={
                                              <Redirect to='/' replace />
                                            }
                                          />

                                          {/* Zodpovedné osoby dostali vlastnú
                                              stránku, kým sa kategória upravovala
                                              v dialógu nad zoznamom. Dnes je oboje
                                              na detaile kategórie, takže stará
                                              adresa vedie oň vyššie - `..` nad
                                              cestou zachová `:categoryId`, takže
                                              odkaz z e-mailu neskončí na 404. */}
                                          <Route
                                            path='/administracia/kategorie-podnetov/:categoryId/zodpovedne-osoby'
                                            element={
                                              <Navigate
                                                to='..'
                                                relative='path'
                                                replace
                                              />
                                            }
                                          />

                                          <Route
                                            path='/profil'
                                            element={<ProfileRedirect />}
                                          />

                                          {/*
                                            Hlavičku stránky si profil kreslí
                                            sám - ľavý pás so sekciami stojí
                                            vedľa nej, nie pod ňou. Detail
                                            v [[ProfilePage]].
                                          */}
                                          <Route
                                            path='/profil/:section'
                                            element={
                                              <RouteMetadata
                                                title={__('Môj profil')}
                                                withoutLayoutPadding
                                                withoutPageHeader
                                              >
                                                <ProfilePage />
                                              </RouteMetadata>
                                            }
                                          />

                                          {/* Routes without cache */}
                                          <Route
                                            element={<QueryProviderNoCache />}
                                          >
                                            <Route
                                              path='/administracia/podnety'
                                              element={
                                                <PermissionsGuard
                                                  hasSomePermission={[
                                                    {
                                                      client_id:
                                                        config.CLIENT_ID,
                                                      permission:
                                                        'pruser:reports:read',
                                                    },
                                                    {
                                                      client_id:
                                                        config.CLIENT_ID,
                                                      permission:
                                                        'pruser:reports:manage',
                                                    },
                                                  ]}
                                                  redirect='/'
                                                >
                                                  {/* Lišta filtrov je v slote
                                                      hlavičky, odsadenie
                                                      tabuľky si stránka kreslí
                                                      sama. */}
                                                  <RouteMetadata
                                                    root
                                                    title={__(
                                                      'Správa podnetov',
                                                    )}
                                                    crumbs={
                                                      ADMIN_CRUMBS.reports
                                                    }
                                                    withoutLayoutPadding
                                                  >
                                                    <ReportsAdminPage />
                                                  </RouteMetadata>
                                                </PermissionsGuard>
                                              }
                                            />
                                            <Route
                                              path='/administracia/podnety/:reportId'
                                              element={
                                                <PermissionsGuard
                                                  hasSomePermission={[
                                                    {
                                                      client_id:
                                                        config.CLIENT_ID,
                                                      permission:
                                                        'pruser:reports:read',
                                                    },
                                                    {
                                                      client_id:
                                                        config.CLIENT_ID,
                                                      permission:
                                                        'pruser:reports:manage',
                                                    },
                                                  ]}
                                                  redirect='/'
                                                >
                                                  <RouteMetadata
                                                    title={__(
                                                      'Správa podnetov',
                                                    )}
                                                    crumbs={
                                                      ADMIN_CRUMBS.reportDetail
                                                    }
                                                    backPath='/administracia/podnety'
                                                  >
                                                    <ReportsDetailPageAdmin />
                                                  </RouteMetadata>
                                                </PermissionsGuard>
                                              }
                                            />
                                            {/* Vybraný nález žije v URL, nie
                                                v stave stránky: na mobile je
                                                detail samostatný krok
                                                navigácie (šípka späť vedie na
                                                obálku) a na desktope tým
                                                vzniká odkaz na konkrétny
                                                nález. Je to tá istá stránka,
                                                len s doplneným parametrom. */}
                                            <Route
                                              path='/administracia/podnety/:reportId/nalezy/:issueId'
                                              element={
                                                <PermissionsGuard
                                                  hasSomePermission={[
                                                    {
                                                      client_id:
                                                        config.CLIENT_ID,
                                                      permission:
                                                        'pruser:reports:read',
                                                    },
                                                    {
                                                      client_id:
                                                        config.CLIENT_ID,
                                                      permission:
                                                        'pruser:reports:manage',
                                                    },
                                                  ]}
                                                  redirect='/'
                                                >
                                                  <RouteMetadata
                                                    title={__(
                                                      'Správa podnetov',
                                                    )}
                                                    crumbs={
                                                      ADMIN_CRUMBS.reportDetail
                                                    }
                                                  >
                                                    <ReportsDetailPageAdmin />
                                                  </RouteMetadata>
                                                </PermissionsGuard>
                                              }
                                            />

                                            <Route
                                              path='/administracia/kategorie-podnetov'
                                              element={
                                                <ReportCategoriesGuard>
                                                  <RouteMetadata
                                                    title={__(
                                                      'Kategórie podnetov',
                                                    )}
                                                    crumbs={
                                                      ADMIN_CRUMBS.reportCategories
                                                    }
                                                    withoutLayoutPadding
                                                  >
                                                    <ReportsCategoriesAdminPage />
                                                  </RouteMetadata>
                                                </ReportCategoriesGuard>
                                              }
                                            />

                                            <Route
                                              path='/administracia/kategorie-podnetov/:categoryId'
                                              element={
                                                <ReportCategoriesGuard>
                                                  <RouteMetadata
                                                    title={__(
                                                      'Detail kategórie',
                                                    )}
                                                    crumbs={
                                                      ADMIN_CRUMBS.reportCategoryDetail
                                                    }
                                                    backPath='/administracia/kategorie-podnetov'
                                                  >
                                                    <ReportCategoryAdminPage />
                                                  </RouteMetadata>
                                                </ReportCategoriesGuard>
                                              }
                                            />

                                            <Route
                                              path='/administracia/ai'
                                              element={
                                                <PermissionsGuard
                                                  hasSomePermission={[
                                                    {
                                                      client_id:
                                                        config.CLIENT_ID,
                                                      permission:
                                                        'pruser:ai-decisions:read',
                                                    },
                                                  ]}
                                                  redirect='/'
                                                >
                                                  <RouteMetadata
                                                    root
                                                    title={__('AI rozhodnutia')}
                                                    crumbs={
                                                      ADMIN_CRUMBS.aiDecisions
                                                    }
                                                    withoutLayoutPadding
                                                  >
                                                    <AiDecisionsAdminPage />
                                                  </RouteMetadata>
                                                </PermissionsGuard>
                                              }
                                            />

                                            <Route
                                              path='/administracia/kategorie-novinky'
                                              element={
                                                <PermissionsGuard
                                                  hasSomePermission={[
                                                    {
                                                      client_id:
                                                        config.CLIENT_ID,
                                                      permission:
                                                        'pruser:news-categories:manage',
                                                    },
                                                  ]}
                                                  redirect='/'
                                                >
                                                  <RouteMetadata
                                                    root
                                                    title={__(
                                                      'Kategórie noviniek',
                                                    )}
                                                    crumbs={
                                                      ADMIN_CRUMBS.newsCategories
                                                    }
                                                    withoutLayoutPadding
                                                  >
                                                    <NewsCategoriesAdminPage />
                                                  </RouteMetadata>
                                                </PermissionsGuard>
                                              }
                                            />

                                            <Route
                                              path='/administracia/kategorie-novinky/:categoryId'
                                              element={
                                                <PermissionsGuard
                                                  hasSomePermission={[
                                                    {
                                                      client_id:
                                                        config.CLIENT_ID,
                                                      permission:
                                                        'pruser:news-categories:manage',
                                                    },
                                                  ]}
                                                  redirect='/'
                                                >
                                                  <RouteMetadata
                                                    title={__(
                                                      'Detail kategórie',
                                                    )}
                                                    crumbs={
                                                      ADMIN_CRUMBS.newsCategoryDetail
                                                    }
                                                    backPath='/administracia/kategorie-novinky'
                                                  >
                                                    <NewsCategoryAdminPage />
                                                  </RouteMetadata>
                                                </PermissionsGuard>
                                              }
                                            />

                                            {/* Zdroje noviniek boli chvíľu samostatná stránka; dnes sú
                                                pravým stĺpcom kategórií noviniek. */}
                                            <Route
                                              path='/administracia/zdroje-noviniek'
                                              element={
                                                <Navigate
                                                  to='/administracia/kategorie-novinky'
                                                  replace
                                                />
                                              }
                                            />

                                            {/* Správa výhod je dočasne skrytá - pozri config.CUSTOM_FEATURES. */}
                                            {config.CUSTOM_FEATURES
                                              .BENEFITS && (
                                              <>
                                                <Route
                                                  path='/administracia/vyhody'
                                                  element={
                                                    <PermissionsGuard
                                                      hasSomePermission={[
                                                        {
                                                          client_id: 'cimpo',
                                                          permission:
                                                            'admin:benefits:manage',
                                                        },
                                                      ]}
                                                      redirect='/'
                                                    >
                                                      <RouteMetadata
                                                        root
                                                        title={__('Vyhody')}
                                                        crumbs={
                                                          ADMIN_CRUMBS.benefits
                                                        }
                                                      >
                                                        <VyhodyAdminPage />
                                                      </RouteMetadata>
                                                    </PermissionsGuard>
                                                  }
                                                />
                                                <Route
                                                  path='/administracia/vyhody/:benefitId'
                                                  element={
                                                    <PermissionsGuard
                                                      hasSomePermission={[
                                                        {
                                                          client_id: 'cimpo',
                                                          permission:
                                                            'admin:benefits:manage',
                                                        },
                                                      ]}
                                                      redirect='/'
                                                    >
                                                      <RouteMetadata
                                                        title={__(
                                                          'Detail výhody',
                                                        )}
                                                        crumbs={
                                                          ADMIN_CRUMBS.benefitDetail
                                                        }
                                                        backPath='/administracia/vyhody'
                                                      >
                                                        <VyhodaDetailAdminPage />
                                                      </RouteMetadata>
                                                    </PermissionsGuard>
                                                  }
                                                />
                                              </>
                                            )}

                                            <Route
                                              path='/administracia/hlasovania'
                                              element={
                                                <PermissionsGuard
                                                  hasSomePermission={[
                                                    {
                                                      client_id:
                                                        config.CLIENT_ID,
                                                      permission:
                                                        'pruser:votings:manage',
                                                    },
                                                  ]}
                                                  redirect='/'
                                                >
                                                  <RouteMetadata
                                                    root
                                                    title={__('Hlasovania')}
                                                    crumbs={
                                                      ADMIN_CRUMBS.votings
                                                    }
                                                    withoutLayoutPadding
                                                  >
                                                    <VotingAdminPage />
                                                  </RouteMetadata>
                                                </PermissionsGuard>
                                              }
                                            />
                                            <Route
                                              path='/administracia/hlasovania/:votingId'
                                              element={
                                                <PermissionsGuard
                                                  hasSomePermission={[
                                                    {
                                                      client_id:
                                                        config.CLIENT_ID,
                                                      permission:
                                                        'pruser:votings:manage',
                                                    },
                                                  ]}
                                                  redirect='/'
                                                >
                                                  <RouteMetadata
                                                    title={__('Hlasovanie')}
                                                    crumbs={
                                                      ADMIN_CRUMBS.votingDetail
                                                    }
                                                    backPath='/administracia/hlasovania'
                                                    /* Builder je dvojpanel na
                                                       celú plochu; odsadenie si
                                                       zápis aj výsledky pridajú
                                                       samy. */
                                                    withoutLayoutPadding
                                                  >
                                                    <VotingDetailAdminPage />
                                                  </RouteMetadata>
                                                </PermissionsGuard>
                                              }
                                            />

                                            {/* Správa projektov je dočasne skrytá - pozri config.CUSTOM_FEATURES. */}
                                            {config.CUSTOM_FEATURES
                                              .PROJECTS && (
                                              <>
                                                <Route
                                                  path='/administracia/projekty'
                                                  element={
                                                    <PermissionsGuard
                                                      hasSomePermission={[
                                                        {
                                                          client_id:
                                                            config.CLIENT_ID,
                                                          permission:
                                                            'pruser:projects:manage',
                                                        },
                                                      ]}
                                                      redirect='/'
                                                    >
                                                      <RouteMetadata
                                                        root
                                                        title={__('Projekty')}
                                                        crumbs={
                                                          ADMIN_CRUMBS.projects
                                                        }
                                                      >
                                                        <ProjectsAdminPage />
                                                      </RouteMetadata>
                                                    </PermissionsGuard>
                                                  }
                                                />
                                                <Route
                                                  path='/administracia/projekty/:projectId'
                                                  element={
                                                    <PermissionsGuard
                                                      hasSomePermission={[
                                                        {
                                                          client_id:
                                                            config.CLIENT_ID,
                                                          permission:
                                                            'pruser:projects:manage',
                                                        },
                                                      ]}
                                                      redirect='/'
                                                    >
                                                      <RouteMetadata
                                                        title={__('Projekt')}
                                                        crumbs={
                                                          ADMIN_CRUMBS.projectDetail
                                                        }
                                                        backPath='/administracia/projekty'
                                                      >
                                                        <ProjectDetailAdminPage />
                                                      </RouteMetadata>
                                                    </PermissionsGuard>
                                                  }
                                                />
                                              </>
                                            )}
                                          </Route>
                                        </Route>

                                        {/*
                                          Adresy spred premenovania podnetov
                                          na podnety. Stoja tu, lebo na ne
                                          ukazujú rozposlané e-maily a push
                                          notifikácie; detail v
                                          [[LegacyRedirect]].
                                        */}
                                        <Route
                                          path='/ohlasenia/nove'
                                          element={
                                            <LegacyRedirect to='/podnety/novy' />
                                          }
                                        />
                                        <Route
                                          path='/ohlasenia/*'
                                          element={
                                            <LegacyRedirect to='/podnety' />
                                          }
                                        />
                                        <Route
                                          path='/moje-ohlasenia/nove'
                                          element={
                                            <LegacyRedirect to='/moje-podnety/novy' />
                                          }
                                        />
                                        <Route
                                          path='/moje-ohlasenia/*'
                                          element={
                                            <LegacyRedirect to='/moje-podnety' />
                                          }
                                        />
                                        <Route
                                          path='/administracia/ohlasenia/*'
                                          element={
                                            <LegacyRedirect to='/administracia/podnety' />
                                          }
                                        />
                                        <Route
                                          path='/administracia/kategorie-ohlasenia/*'
                                          element={
                                            <LegacyRedirect to='/administracia/kategorie-podnetov' />
                                          }
                                        />

                                        <Route
                                          path='*'
                                          element={<NotFoundPage />}
                                        />
                                      </Routes>
                                    </Layout>
                                  </HomePageLocationProvider>
                                </NotificationsProvider>
                              </GamificationProvider>
                            </RolesProvider>
                          </LayoutProvider>
                        </BrowserRouter>
                      </GeoLocationProvider>
                    </SnackbarProvider>
                  </WebPushProvider>
                </MqttClientProvider>
              </ResponsiveProvider>
            </ThemeProvider>
          </NetworkProvider>
        </LoggerProvider>
      </AuthProvider>
    </QueryClientProvider>
  );
};

export default App;
